// PhotoSwipe component used for the Lightbox functionality
// Web: https://photoswipe.com/
// Doc: https://photoswipe.com/documentation/getting-started.html

// Source: https://github.com/dimsemenov/photoswipe
// import PhotoSwipe from 'photoswipe';
// import PhotoSwipe UI_Default from 'photoswipe/dist/photoswipe-ui-default';

import * as PhotoSwipe from 'photoswipe';
import * as PhotoSwipeUI_Default from 'photoswipe/dist/photoswipe-ui-default';

import { getChildren, getClosesParent, getRandomString } from '../helpers/generic';

export type TPhotoSwipeGalleryContext = 'relatedImages' | 'photoGalleryImages' | 'randomPhotoGalleryImages'; // list of supported templates

interface IPhotoSwipeItemExtended extends PhotoSwipe.Item {
    title?: string;
    el?: Element;
}

// to open single image in the lightbox
export const initPhotoSwipeSingleImage = (imageLink: HTMLElement): void => {
    let gallery: PhotoSwipe<any>;

    // triggers when user clicks on thumbnail
    const onThumbnailsClick = (e: Event): false => {
        e = e || window.event;
        e.preventDefault ? e.preventDefault() : (e.returnValue = false);
        // open PhotoSwipe if valid index found
        openPhotoSwipe(0, imageLink as HTMLElement);
        return false;
    };

    const openPhotoSwipe = (
        index: number,
        imageLink: HTMLElement,
        disableAnimation?: boolean,
        fromURL?: boolean,
    ): void => {
        const templateElement: HTMLElement = document.querySelectorAll('.pswp')[0] as HTMLElement;
        const dataSize = imageLink.getAttribute('data-size');
        const size = dataSize ? dataSize.split('x') : ['0', '0'];
        const items: Array<IPhotoSwipeItemExtended> = [
            {
                src: imageLink.getAttribute('href') || undefined,
                w: parseInt(size[0], 10),
                h: parseInt(size[1], 10),
                title: imageLink.getAttribute('title') || '',
            },
        ];

        // define options (if needed)
        const options: PhotoSwipe.Options = {
            // define gallery index (for URL)
            galleryUID: parseInt(imageLink.getAttribute('data-pswp-uid') as string),
            index: 1,
        };

        if (disableAnimation) {
            options.showAnimationDuration = 0;
        }

        // Pass data to PhotoSwipe and initialize it
        gallery = new PhotoSwipe(templateElement, PhotoSwipeUI_Default, items, options);
        gallery.init();

        fixZeroDimensions(gallery);
    };

    let guid = imageLink.getAttribute('data-pswp-uid');
    if (!guid) {
        guid = getRandomString(10);
        imageLink.setAttribute('data-pswp-uid', guid);
    }
    imageLink.onclick = onThumbnailsClick;

    // Parse URL and open gallery if it contains #&pid=xxx&gid=1
    const hashData = photoswipeParseHash();
    if (hashData.pid && hashData.gid && hashData.gid === guid) {
        openPhotoSwipe(hashData.pid, imageLink, true, true);
    }
};

// to open group of images in a gallery lightbox
export const initPhotoSwipeGallery = (gallerySelector: string, context: TPhotoSwipeGalleryContext): void => {
    const GALLERY_CONTEXT = context || 'relatedImages';

    // parse slide data (url, title, size ...) from DOM elements
    // (children of gallerySelector)
    const parseThumbnailElements = (galleryElement: HTMLElement): Array<IPhotoSwipeItemExtended> | null => {
        if (GALLERY_CONTEXT === 'relatedImages') {
            return parseThumbnailElementsFromRelatedImages(galleryElement);
        }
        if (GALLERY_CONTEXT === 'photoGalleryImages') {
            return parseThumbnailElementsFromPhotoGalleryImages(galleryElement);
        }
        if (GALLERY_CONTEXT === 'randomPhotoGalleryImages') {
            return parseThumbnailElementsFromRandomPhotoGalleryImages(galleryElement);
        }
        return null;
    };

    // Specific parser method for RELATED IMAGES gallery (based on template structure from www_related-files.inc.html.tpl
    const parseThumbnailElementsFromRelatedImages = (galleryElement: HTMLElement): Array<IPhotoSwipeItemExtended> => {
        const listElement = getChildren(galleryElement, '.related-files__list')[0];
        const listItemElements = getChildren(listElement, '.related-files__item'); //listElement.childNodes; // list items
        const numNodes = listItemElements.length;
        const items: Array<IPhotoSwipeItemExtended> = [];
        let figureEl: HTMLElement;
        let linkEl: HTMLLinkElement;
        let size: Array<string>;
        let item: IPhotoSwipeItemExtended;

        for (let i = 0; i < numNodes; i++) {
            figureEl = getChildren(listItemElements[i] as Element, '.related-files__item-figure')[0] as HTMLElement; // <figure> element
            // include only element nodes
            if (figureEl.nodeType !== 1) {
                continue;
            }

            linkEl = figureEl.children[0] as HTMLLinkElement; // <a> element
            const dataSize = linkEl.getAttribute('data-size');
            size = dataSize ? dataSize.split('x') : ['0', '0'];

            // create slide object
            item = {
                src: linkEl.getAttribute('href') as string,
                w: parseInt(size[0], 10),
                h: parseInt(size[1], 10),
            };

            if (figureEl.children.length > 1) {
                // <figcaption> content
                // item.title = figureEl.children[1].innerHTML;
                item.title = getChildren(figureEl.children[1], '.related-files__item-title')[0]?.innerHTML || '';
            }

            if (linkEl.children.length > 0) {
                // <img> thumbnail element, retrieving thumbnail url
                const thumbElement = getChildren(linkEl, '.related-files__item-thumb')[0];
                if (thumbElement) {
                    item.msrc = thumbElement.children[0].getAttribute('src') as string;
                }
            }

            item.el = figureEl; // save link to element for getThumbBoundsFn
            items.push(item);
        }

        return items;
    };

    // Specific parser method for gallery list (based on template structure from www_module_photogallery.prehled-fotografii.inc.html.tpl
    const parseThumbnailElementsFromPhotoGalleryImages = (
        galleryElement: HTMLElement,
    ): Array<IPhotoSwipeItemExtended> => {
        const listElement = galleryElement;
        const listItemElements = getChildren(listElement, '.photo-gallery__item'); // list items
        const numNodes = listItemElements.length;
        const items: Array<IPhotoSwipeItemExtended> = [];
        let figureEl: HTMLElement;
        let linkEl: HTMLLinkElement;
        let size: Array<string>;
        let item: IPhotoSwipeItemExtended;

        for (let i = 0; i < numNodes; i++) {
            figureEl = getChildren(listItemElements[i] as Element, '.photo-gallery__item-figure')[0] as HTMLElement; // <figure> element
            // include only element nodes
            if (figureEl.nodeType !== 1) {
                continue;
            }

            linkEl = figureEl.children[0] as HTMLLinkElement; // <a> element
            const dataSize = linkEl.getAttribute('data-size');
            size = dataSize ? dataSize.split('x') : ['0', '0'];

            // create slide object
            item = {
                src: linkEl.getAttribute('href') as string,
                w: parseInt(size[0], 10),
                h: parseInt(size[1], 10),
            };

            if (figureEl.children.length > 1) {
                // <figcaption> content
                // item.title = figureEl.children[1].innerHTML;
                item.title = getChildren(figureEl.children[1], '.photo-gallery__item-title')[0]?.innerHTML || '';
            }

            if (linkEl.children.length > 0) {
                // <img> thumbnail element, retrieving thumbnail url
                const thumbElement = getChildren(linkEl, '.photo-gallery__item-thumb')[0];
                const mSrc = thumbElement && thumbElement.getAttribute('data-src');
                if (mSrc) {
                    item.msrc = mSrc;
                }
            }

            item.el = figureEl; // save link to element for getThumbBoundsFn
            items.push(item);
        }

        return items;
    };

    // Specific parser method for GRID IMAGES gallery (based on template structure from www_module_photogallery.nahodny-obrazek-grid.inc.html.tpl
    const parseThumbnailElementsFromRandomPhotoGalleryImages = (
        galleryElement: HTMLElement,
    ): Array<IPhotoSwipeItemExtended> => {
        const listElement = galleryElement;
        const listItemElements = getChildren(listElement, '.random-photo__item'); // list items
        const numNodes = listItemElements.length;
        const items: Array<IPhotoSwipeItemExtended> = [];
        let figureEl: HTMLElement;
        let linkEl: HTMLLinkElement;
        let size: Array<string>;
        let item: IPhotoSwipeItemExtended;

        for (let i = 0; i < numNodes; i++) {
            figureEl = getChildren(listItemElements[i] as Element, '.random-photo__figure')[0] as HTMLElement; // <figure> element
            // include only element nodes
            if (figureEl.nodeType !== 1) {
                continue;
            }

            linkEl = figureEl.children[0] as HTMLLinkElement; // <a> element
            const dataSize = linkEl.getAttribute('data-size');
            size = dataSize ? dataSize.split('x') : ['0', '0'];

            // create slide object
            item = {
                src: linkEl.getAttribute('href') as string,
                w: parseInt(size[0], 10),
                h: parseInt(size[1], 10),
            };

            if (figureEl.children.length > 1) {
                // <figcaption> content
                // item.title = figureEl.children[1].innerHTML;
                item.title = getChildren(figureEl.children[1], '.random-photo__item-title')[0]?.innerHTML || '';
            }

            if (linkEl.children.length > 0) {
                // <img> thumbnail element, retrieving thumbnail url
                const thumbElement = getChildren(linkEl, '.random-photo__img')[0];
                const mSrc = thumbElement && thumbElement.getAttribute('data-src');
                if (mSrc) {
                    item.msrc = mSrc;
                }
            }

            item.el = figureEl; // save link to element for getThumbBoundsFn
            items.push(item);
        }

        return items;
    };

    // triggers when user clicks on thumbnail
    const onThumbnailsClick = (e: Event): false => {
        e = e || window.event;
        const eTarget = e.target || e.srcElement;
        // u elementu s data-skip-pswp-action preskocime akci vytvoreni galerie => to budou asi bezne odkazy nekam
        if (!(eTarget as HTMLElement).hasAttribute('data-skip-pswp-action')) {
            e.preventDefault ? e.preventDefault() : (e.returnValue = false);
            // find root element of slide (= LI item)
            const clickedListItem = getClosesParent(eTarget as Node, function (el: HTMLElement) {
                return !!(el.tagName && el.tagName.toUpperCase() === 'LI');
            });

            if (!clickedListItem) {
                return false;
            }

            // find index of clicked item by looping through all child nodes
            // alternatively, you may define index via data-attribute
            const clickedGallery = getClosesParent(clickedListItem as Node, function (el: HTMLElement) {
                return el.hasAttribute('data-pswp-uid');
            });
            const childNodes = (clickedListItem.parentNode as ParentNode).childNodes;
            const numChildNodes = childNodes.length;
            let nodeIndex = 0;
            let index = 0;

            for (let i = 0; i < numChildNodes; i++) {
                if (childNodes[i].nodeType !== 1) {
                    continue;
                }

                if (childNodes[i] === clickedListItem) {
                    index = nodeIndex;
                    break;
                }
                nodeIndex++;
            }

            if (index >= 0) {
                // open PhotoSwipe if valid index found
                openPhotoSwipe(index, clickedGallery as HTMLElement);
            }

            return false;
        }
        return false;
    };

    const openPhotoSwipe = (
        index: number,
        galleryElement: HTMLElement,
        disableAnimation?: boolean,
        fromURL?: boolean,
    ): void => {
        const templateElement = document.querySelectorAll('.pswp')[0] as HTMLElement;
        const items = parseThumbnailElements(galleryElement);
        if (!items) {
            return;
        }
        // define options (if needed)
        const options: PhotoSwipe.Options = {
            // define gallery index (for URL)
            galleryUID: parseInt(galleryElement.getAttribute('data-pswp-uid') as string),
            getThumbBoundsFn: (index: number) => {
                // See Options -> getThumbBoundsFn section of documentation for more info
                const thumbnail = (items[index].el as Element).getElementsByTagName('img')[0]; // find thumbnail
                const pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
                const rect = thumbnail.getBoundingClientRect();
                return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
            },
        };

        // PhotoSwipe opened from URL
        if (fromURL) {
            if (options.galleryPIDs) {
                // parse real index when custom PIDs are used
                // http://photoswipe.com/documentation/faq.html#custom-pid-in-url
                for (let j = 0; j < items.length; j++) {
                    if (items[j].pid == index) {
                        options.index = j;
                        break;
                    }
                }
            } else {
                // in URL indexes start from 1
                options.index = parseInt(`${index}`, 10) - 1;
            }
        } else {
            options.index = parseInt(`${index}`, 10);
        }

        // exit if index not found
        if (isNaN(<number>options.index)) {
            return;
        }

        if (disableAnimation) {
            options.showAnimationDuration = 0;
        }

        // Pass data to PhotoSwipe and initialize it
        const gallery = new PhotoSwipe(templateElement, PhotoSwipeUI_Default, items, options);
        gallery.init();

        fixZeroDimensions(gallery);
    };

    // loop through all gallery elements and bind events
    const galleryElements: NodeListOf<HTMLElement> = document.querySelectorAll(gallerySelector);
    for (let i = 0, l = galleryElements.length; i < l; i++) {
        galleryElements[i].setAttribute('data-pswp-uid', `${i + 1}`);
        galleryElements[i].onclick = onThumbnailsClick;
    }

    // Parse URL and open gallery if it contains #&pid=3&gid=1
    const hashData = photoswipeParseHash();
    if (hashData.pid && hashData.gid) {
        openPhotoSwipe(hashData.pid, galleryElements[hashData.gid - 1], true, true);
    }
};

// parse picture index and gallery index from URL (#&pid=1&gid=2)
const photoswipeParseHash = (): any => {
    const hash = window.location.hash.substring(1);
    const params = {};

    if (hash.length < 5) {
        return params;
    }

    const vars = hash.split('&');
    for (let i = 0; i < vars.length; i++) {
        if (!vars[i]) {
            continue;
        }
        const pair = vars[i].split('=');
        if (pair.length < 2) {
            continue;
        }
        if (pair && pair[0]) {
            // @ts-ignore
            params[pair[0]] = pair[1];
        }
    }

    // if (params.gid) {
    //     params.gid = parseInt(params.gid, 10);
    // }

    return params;
};

// Fix for undefined width and height attributes which are needed
// See https://github.com/dimsemenov/PhotoSwipe/issues/796 + https://github.com/dimsemenov/PhotoSwipe/issues/1149
const fixZeroDimensions = (gallery: PhotoSwipe<PhotoSwipe.Options>): void => {
    gallery.listen('imageLoadComplete', function (index: number, item: PhotoSwipe.Item) {
        if (!item.w || item.w < 1 || !item.h || item.h < 1) {
            const img: HTMLImageElement = new Image();
            img.onload = function (this: GlobalEventHandlers, ev: Event): void {
                item.w = img.width;
                item.h = img.height;
                gallery.updateSize(true);
            };
            img.src = item.src as string;
        }
    });
};
